import _ from "lodash";
import moment from "moment";
import { IAccount, ICustomerDetail, ISetCustomerData } from "@interfaces";
import {
    hasOnlySignupDetails,
    hasPlaceholderProfile,
    isPlaceholderDob,
    isPlaceholderName,
    isPlaceholderPhone,
    isPlaceholderPostCode,
} from "./signupOfferConfig";

/** Formats the backend is known to serve a stored date of birth in. */
const DOB_FORMATS = ["DD-MMM-YYYY", "YYYY-MM-DD", moment.ISO_8601];

/**
 * A saved traveller's details, whichever record they came from.
 *
 * A suggestion carries the real values on a nested `customer` (the account
 * holder) or `guest` (an address-book entry), and repeats a flattened copy on the
 * row itself under slightly different names. The nested record wins where it
 * exists because the flat copy is lossy — the phone has been through `Number()`.
 *
 * For the account holder, a live `account` beats both. That row is only ever a
 * view of the profile, and the profile in Redux is reloaded the instant it is
 * corrected, whereas the nested copy is a snapshot from whenever the suggestion
 * list was last fetched. Without this, correcting the traveller on one product in
 * a multi-product checkout leaves every other product still reading the
 * stand-ins, and asking for the same details again.
 */
export const resolveTravellerDetails = (
    traveller: ICustomerDetail,
    account?: IAccount | null,
) => {
    const live = traveller.is_primary ? account : undefined;
    const details = traveller.is_primary ? traveller.customer : traveller.guest;
    return {
        title: live?.title ?? traveller.title,
        countryCode: live?.country_code ?? details?.country_code ?? traveller.country_code,
        firstName: live?.first_name ?? details?.first_name ?? traveller.first_name,
        lastName: live?.last_name ?? details?.last_name ?? traveller.last_name,
        email: live?.email ?? details?.email ?? traveller.email,
        dateOfBirth: live?.date_of_birth ?? details?.date_of_birth ?? traveller.date_of_birth,
        phoneNumber: live?.phone_number ?? details?.phone_number ?? traveller.phone_number,
        postCode: live?.post_code ?? details?.post_code ?? traveller.postal_code,
    };
};

/**
 * Whether a saved traveller still holds any sign-up stand-in, and so must not be
 * added to a booking as they are.
 *
 * These records were created from a profile that had been filled with stand-ins
 * to get the account upgraded, so adding one would send a 1900 date of birth and
 * a zeroed phone number to the supplier as if they were real.
 */
export const travellerNeedsDetailReview = (
    traveller: ICustomerDetail,
    account?: IAccount | null,
): boolean => {
    const d = resolveTravellerDetails(traveller, account);
    return hasPlaceholderProfile({
        first_name: d.firstName,
        last_name: d.lastName,
        date_of_birth: d.dateOfBirth,
        phone_number: d.phoneNumber,
        post_code: d.postCode,
    });
};

/**
 * Seed the traveller form from a record that needs correcting, so the form's
 * required-field validation makes the visitor supply a real value for anything
 * that is still a stand-in before the traveller can be saved.
 *
 * Which fields survive depends on whether a stand-in can be told apart from a
 * real answer:
 *
 * - The email was collected from the visitor at sign-up, so it is always real and
 *   always kept.
 * - The names have their own stand-ins now that the sheet no longer asks for
 *   them, and nobody is really called Guest Traveller, so they are recognisable
 *   and tested like the numbers below.
 * - The date of birth, phone and post code have unmistakable stand-ins, so they
 *   are tested one by one — a record that has already been partly corrected
 *   keeps whichever of them are genuine.
 * - Title and country are kept only if the record has something real beyond the
 *   three sign-up fields. "Mr" and "AU" are ordinary real answers, so a
 *   stand-in cannot be told from a choice; but a record holding nothing but the
 *   sign-up three cannot have had either chosen, while one with a real date of
 *   birth, phone or post code has been through this form before and its title
 *   and country are the visitor's own.
 */
export const travellerToReviewPrefill = (
    traveller: ICustomerDetail,
    account?: IAccount | null,
): Partial<ISetCustomerData> => {
    const d = resolveTravellerDetails(traveller, account);
    const seeded: Partial<ISetCustomerData> = {};
    if (d.firstName && !isPlaceholderName("first_name", d.firstName))
        seeded.firstName = d.firstName;
    if (d.lastName && !isPlaceholderName("last_name", d.lastName))
        seeded.lastName = d.lastName;
    if (d.email) seeded.email = d.email;
    if (d.dateOfBirth && !isPlaceholderDob(d.dateOfBirth)) {
        // The picker reads and writes YYYY-MM-DD, but the stored value can be in
        // any of the formats the backend serves, so normalise rather than hand it
        // a string moment would have to guess at.
        const parsed = moment(d.dateOfBirth, DOB_FORMATS, true);
        if (parsed.isValid()) seeded.dateOfBirth = parsed.format("YYYY-MM-DD");
    }
    if (!_.isNil(d.phoneNumber) && !isPlaceholderPhone(d.phoneNumber)) {
        seeded.phoneNumber = String(d.phoneNumber);
    }
    if (d.postCode && !isPlaceholderPostCode(d.postCode)) seeded.postalCode = d.postCode;
    if (
        !hasOnlySignupDetails({
            first_name: d.firstName,
            last_name: d.lastName,
            email: d.email,
            date_of_birth: d.dateOfBirth,
            phone_number: d.phoneNumber,
            post_code: d.postCode,
        })
    ) {
        if (d.title) seeded.title = d.title;
        if (d.countryCode) seeded.countryCode = d.countryCode;
    }
    return seeded;
};
